import React, { useState } from "react";
import "../styles/GameScreen.css";
import StatsPanel from "./StatsPanel";
import CombatScreen from "./CombatScreen";
import Market from "./Market";
import Inventory from "./Inventory";
import Help from "./Help";
import StreetsMenu from "./menus/StreetsMenu";
import LocationMenu from "./menus/LocationMenu";
import { FaBars } from "react-icons/fa";

function GameScreen({ gameState, setGameState, onUpdateCharacter }) {
  const [currentScreen, setCurrentScreen] = useState("streets");
  const [showInventory, setShowInventory] = useState(false);
  const [showMobileStats, setShowMobileStats] = useState(false);
  const [marketParams, setMarketParams] = useState({
    title: "Black Market",
    locationFilter: null,
  });
  const [combatParams, setCombatParams] = useState({
    locationName: "Streets",
    locationId: null,
    enemyIds: [],
  });

  const handleNavigate = (destination, params = {}) => {
    if (destination === "market" && params) {
      // Store market parameters
      setMarketParams({
        title: params.title || "Black Market",
        locationFilter: params.locationFilter || null,
      });
    }
    if (destination === "combat" && params) {
      // Store combat parameters
      setCombatParams({
        locationName: params.locationName || "Streets",
        locationId: params.locationId || null,
        enemyIds: params.enemyIds || [],
      });
    }
    setCurrentScreen(destination);
  };

  const handleShowInventory = () => {
    setShowInventory(!showInventory);
  };

  const handleToggleMobileStats = () => {
    setShowMobileStats(!showMobileStats);
  };

  const handleCombatEnd = (result, rewards, droppedItem) => {
    console.log("Combat ended with result:", result, "and rewards:", rewards);
    console.log("Dropped item:", droppedItem);
    console.log(
      "Current character credits before update:",
      gameState.character?.credits
    );
    console.log(
      "Current character experience before update:",
      gameState.character?.experience
    );
    console.log("Rewards experience:", rewards?.experience);

    setCurrentScreen("streets");
    if (result === "victory" && rewards) {
      console.log("Processing victory rewards...");
      setGameState((prev) => {
        // Ensure experience is a number, default to 0 if undefined/null
        const currentExp =
          typeof prev.character.experience === "number"
            ? prev.character.experience
            : 0;
        const rewardExp =
          typeof rewards.experience === "number" ? rewards.experience : 0;

        const updatedCharacter = {
          ...prev.character,
          experience: currentExp + rewardExp,
        };

        // Use safe credit management
        if (prev.character.gainCredits) {
          const creditResult = prev.character.gainCredits.call(
            updatedCharacter,
            rewards.credits,
            "Combat victory"
          );
          updatedCharacter.credits = creditResult.totalCredits;
        } else {
          // Fallback if gainCredits method doesn't exist
          updatedCharacter.credits = prev.character.credits + rewards.credits;
        }

        // Add dropped item to inventory if one was dropped
        if (droppedItem) {
          const itemWithInventoryId = {
            ...droppedItem,
            inventoryId: `inv_${Date.now()}_${Math.random()
              .toString(36)
              .substr(2, 9)}`, // Unique inventory ID
          };
          updatedCharacter.inventory = [
            ...updatedCharacter.inventory,
            itemWithInventoryId,
          ];
          console.log("Added dropped item to inventory:", itemWithInventoryId);
        }

        console.log("Updated character credits:", updatedCharacter.credits);
        console.log(
          "Updated character experience:",
          updatedCharacter.experience
        );
        return {
          ...prev,
          character: updatedCharacter,
        };
      });
    }
  };

  const handleCharacterUpdate = (updatedCharacter) => {
    // Use the passed onUpdateCharacter function to save to localStorage
    if (onUpdateCharacter) {
      onUpdateCharacter(updatedCharacter);
    }

    setGameState((prev) => ({
      ...prev,
      character: updatedCharacter,
    }));
  };

  const renderLocation = () => {
    switch (currentScreen) {
      case "combat":
        return (
          <CombatScreen
            character={gameState.character}
            onCombatEnd={handleCombatEnd}
            onUpdateCharacter={handleCharacterUpdate}
            locationName={combatParams.locationName}
            locationId={combatParams.locationId}
            enemyIds={combatParams.enemyIds}
          />
        );

      case "market":
        return (
          <Market
            character={gameState.character}
            onExit={() => handleNavigate("streets")}
            onUpdateCharacter={handleCharacterUpdate}
            onNavigate={handleNavigate}
            title={marketParams.title}
            locationFilter={marketParams.locationFilter}
          />
        );

      case "inventory":
        return (
          <Inventory
            character={gameState.character}
            onExit={() => handleNavigate("streets")}
            onUpdateCharacter={handleCharacterUpdate}
            onNavigate={handleNavigate}
          />
        );

      case "help":
        return <Help onExit={() => handleNavigate("streets")} />;

      case "streets":
        return (
          <StreetsMenu
            character={gameState.character}
            onNavigate={handleNavigate}
            onShowInventory={handleShowInventory}
            onToggleMobileStats={handleToggleMobileStats}
            showMobileStats={showMobileStats}
          />
        );

      // All locations use the dynamic LocationMenu component
      // This handles navigation based on the locations array in locations.js
      default:
        // Check if it's a valid location from locations.js
        if (currentScreen && typeof currentScreen === "string") {
          return (
            <LocationMenu
              locationId={currentScreen}
              character={gameState.character}
              onNavigate={handleNavigate}
              onShowInventory={handleShowInventory}
              onToggleMobileStats={handleToggleMobileStats}
              showMobileStats={showMobileStats}
              onUpdateCharacter={handleCharacterUpdate}
            />
          );
        }
        return (
          <div className="location-screen">
            <div className="location-header">
              <h2>Unknown Location</h2>
            </div>
            <div className="location-description">
              This area of Night City hasn't been fully mapped yet. The
              corporate data feeds are corrupted, and local intel is scarce. You
              could be anywhere - a restricted zone, a corporate black site, or
              somewhere that doesn't officially exist. Best to head back to
              familiar territory before you stumble into something you can't
              handle.
            </div>
            <div className="options-grid">
              <div className="option-row">
                <span
                  className="menu-item"
                  onClick={() => handleNavigate("streets")}
                >
                  Back to Streets
                </span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="game-screen">
      <div className="game-layout">
        <div className="main-content">
          {/* Mobile Stats Toggle Button */}
          <div className="stats-toggle">
            <button
              className="stats-button"
              onClick={handleToggleMobileStats}
              title="Toggle Stats Panel"
            >
              <FaBars />
            </button>
          </div>
          {renderLocation()}
        </div>
        <StatsPanel
          character={gameState.character}
          onUpdateCharacter={handleCharacterUpdate}
          showMobileStats={showMobileStats}
          onNavigate={handleNavigate}
          onCloseMobileStats={() => setShowMobileStats(false)}
        />
      </div>
    </div>
  );
}

export default GameScreen;
